/*
 * Design: Gradient Noir – Stripe-inspired dark nav with glassmorphism
 * Sticky navbar with backdrop-blur, brand color accents, mega menu on services hover
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "wouter";
import { Menu, X, ChevronDown, Globe, Search, Megaphone, Share2, Bot, Shield, ArrowLeft, ExternalLink } from "lucide-react";

const LOGO_URL = "https://d2xsxph8kpxj0f.cloudfront.net/310519663396493078/EDyimBtawRo3FYhzWUwDs6/upsite_white_3261f55b.png";

const navLinks = [
  { href: "/", label: "דף הבית" },
  { href: "/about", label: "אודות" },
  { href: "/services", label: "שירותים", hasMega: true },
  { href: "/clients", label: "לקוחות" },
  { href: "/projects", label: "פרויקטים" },
  { href: "/blog", label: "בלוג" },
  { href: "/contact", label: "צור קשר" },
];

const coreServices = [
  {
    href: "/services/web-development",
    icon: Globe,
    title: "בניית אתרים",
    desc: "פיתוח אתרים מודרניים שמותאמים לקידום ולהמרות.",
  },
  {
    href: "/services/seo",
    icon: Search,
    title: "קידום אורגני (SEO)",
    desc: "הגדלת חשיפה בגוגל באמצעות אסטרטגיית SEO מקצועית.",
  },
];

const additionalServices = [
  {
    href: "/services/paid-advertising",
    icon: Megaphone,
    title: "קידום ממומן",
    desc: "ניהול קמפיינים בגוגל וברשתות החברתיות.",
  },
  {
    href: "/services/social-media",
    icon: Share2,
    title: "ניהול סושיאל",
    desc: "ניהול תוכן ונוכחות ברשתות החברתיות.",
  },
  {
    href: "/services/ai-marketing",
    icon: Bot,
    title: "קידום באמצעות AI",
    desc: "שימוש בכלי AI לייעול תהליכי שיווק וצמיחה.",
  },
  {
    href: "https://upsiteorm.co.il/",
    icon: Shield,
    title: "ניהול מוניטין",
    desc: "ניהול ושיפור המוניטין הדיגיטלי של העסק שלכם.",
    external: true,
  },
];

interface MegaMenuServiceItem {
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  desc: string;
  external?: boolean;
}

function ServiceMenuLink({ s }: { s: MegaMenuServiceItem }) {
  if (s.external) {
    return (
      <a
        href={s.href}
        target="_blank"
        rel="noopener noreferrer"
        role="menuitem"
        className="flex gap-3 p-3 rounded-xl hover:bg-white/5 focus:bg-white/5 focus:outline-none transition-colors group"
      >
        <div className="w-10 h-10 rounded-lg bg-[#66CDAA]/10 flex items-center justify-center shrink-0 group-hover:bg-[#66CDAA]/20 transition-colors">
          <s.icon className="w-5 h-5 text-[#66CDAA]" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-white group-hover:text-[#66CDAA] transition-colors flex items-center gap-1.5">
            {s.title}
            <ExternalLink className="w-3 h-3 text-white/40 group-hover:text-[#66CDAA]/60" />
          </div>
          <div className="text-xs text-white/50 mt-0.5 leading-relaxed">
            {s.desc}
          </div>
        </div>
      </a>
    );
  }
  return (
    <Link
      href={s.href}
      role="menuitem"
      className="flex gap-3 p-3 rounded-xl hover:bg-white/5 focus:bg-white/5 focus:outline-none transition-colors group"
    >
      <div className="w-10 h-10 rounded-lg bg-[#66CDAA]/10 flex items-center justify-center shrink-0 group-hover:bg-[#66CDAA]/20 transition-colors">
        <s.icon className="w-5 h-5 text-[#66CDAA]" />
      </div>
      <div>
        <div className="text-sm font-semibold text-white group-hover:text-[#66CDAA] transition-colors">
          {s.title}
        </div>
        <div className="text-xs text-white/50 mt-0.5 leading-relaxed">
          {s.desc}
        </div>
      </div>
    </Link>
  );
}

function MobileServiceLink({ s }: { s: MegaMenuServiceItem }) {
  if (s.external) {
    return (
      <a
        href={s.href}
        target="_blank"
        rel="noopener noreferrer"
        className="flex gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors"
      >
        <s.icon className="w-5 h-5 text-[#66CDAA] shrink-0 mt-0.5" />
        <div>
          <div className="text-sm font-semibold text-white flex items-center gap-1.5">
            {s.title}
            <ExternalLink className="w-3 h-3 text-white/40" />
          </div>
          <div className="text-xs text-white/50">{s.desc}</div>
        </div>
      </a>
    );
  }
  return (
    <Link
      href={s.href}
      className="flex gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors"
    >
      <s.icon className="w-5 h-5 text-[#66CDAA] shrink-0 mt-0.5" />
      <div>
        <div className="text-sm font-semibold text-white">{s.title}</div>
        <div className="text-xs text-white/50">{s.desc}</div>
      </div>
    </Link>
  );
}

export default function Navbar() {
  const [location] = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileMegaOpen, setMobileMegaOpen] = useState(false);
  const megaMenuRef = useRef<HTMLDivElement>(null);

  const closeMega = useCallback(() => setMegaOpen(false), []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMegaOpen(false);
    setMobileMegaOpen(false);
  }, [location]);

  // Close mega menu on Escape
  useEffect(() => {
    if (!megaOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMega();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [megaOpen, closeMega]);

  return (
    <nav
      className={`fixed top-0 right-0 left-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#0F172A]/80 backdrop-blur-xl border-b border-white/5 shadow-lg shadow-black/20"
          : "bg-transparent"
      }`}
    >
      <div className="container">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link href="/" className="flex items-center group shrink-0">
            <img
              src={LOGO_URL}
              alt="Upsite – אפסייט"
              fetchPriority="high"
              className="h-9 sm:h-10 w-auto object-contain group-hover:opacity-80 transition-opacity"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) =>
              link.hasMega ? (
                <div
                  key={link.href}
                  className="relative"
                  ref={megaMenuRef}
                  onMouseEnter={() => setMegaOpen(true)}
                  onMouseLeave={() => setMegaOpen(false)}
                >
                  <button
                    type="button"
                    aria-expanded={megaOpen}
                    aria-haspopup="true"
                    onClick={() => setMegaOpen((prev) => !prev)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setMegaOpen((prev) => !prev);
                      }
                    }}
                    className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      location.startsWith("/services")
                        ? "text-[#66CDAA]"
                        : "text-white/70 hover:text-white"
                    }`}
                  >
                    {link.label}
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${megaOpen ? "rotate-180" : ""}`}
                    />
                  </button>

                  {/* Mega Menu */}
                  <div
                    role="menu"
                    aria-label="שירותים"
                    {...(!megaOpen && { inert: true })}
                    className={`absolute top-full right-0 pt-4 transition-all duration-300 ${
                      megaOpen
                        ? "opacity-100 translate-y-0 pointer-events-auto"
                        : "opacity-0 -translate-y-2 pointer-events-none"
                    }`}
                  >
                    <div className="w-[520px] bg-[#0F172A] border border-white/10 rounded-2xl p-6 shadow-2xl shadow-black/40">
                      <div className="grid grid-cols-2 gap-6">
                        {/* Column 1 – Core */}
                        <div>
                          <h4 className="text-xs font-semibold text-[#66CDAA] uppercase tracking-wider mb-4">
                            שירותי ליבה
                          </h4>
                          <div className="space-y-3">
                            {coreServices.map((s) => (
                              <ServiceMenuLink key={s.href} s={s} />
                            ))}
                          </div>
                        </div>

                        {/* Column 2 – Additional */}
                        <div>
                          <h4 className="text-xs font-semibold text-[#66CDAA] uppercase tracking-wider mb-4">
                            שירותי שיווק נוספים
                          </h4>
                          <div className="space-y-3">
                            {additionalServices.map((s) => (
                              <ServiceMenuLink key={s.href} s={s} />
                            ))}
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={link.href}
                  href={link.href}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    location === link.href
                      ? "text-[#66CDAA]"
                      : "text-white/70 hover:text-white"
                  }`}
                >
                  {link.label}
                </Link>
              )
            )}
          </div>

          {/* CTA + Mobile Toggle */}
          <div className="flex items-center gap-3">
            <Link
              href="/contact"
              className="hidden lg:inline-block glow-btn text-sm px-6 py-2.5"
            >
              דברו איתנו
            </Link>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg text-white hover:bg-white/10 transition-colors"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden transition-all duration-300 overflow-hidden ${
          mobileOpen ? "max-h-[80vh] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="bg-[#0F172A]/95 backdrop-blur-xl border-t border-white/5 px-4 py-6 space-y-1">
          {mobileMegaOpen ? (
            <div className="space-y-2">
              <button
                onClick={() => setMobileMegaOpen(false)}
                className="flex items-center gap-2 text-[#66CDAA] text-sm font-medium mb-4"
              >
                <ArrowLeft className="w-4 h-4" />
                חזרה
              </button>
              <h4 className="text-xs font-semibold text-[#66CDAA] uppercase tracking-wider mb-2">שירותי ליבה</h4>
              {coreServices.map((s) => (
                <MobileServiceLink key={s.href} s={s} />
              ))}
              <h4 className="text-xs font-semibold text-[#66CDAA] uppercase tracking-wider mt-4 mb-2">שירותים נוספים</h4>
              {additionalServices.map((s) => (
                <MobileServiceLink key={s.href} s={s} />
              ))}
            </div>
          ) : (
            <>
              {navLinks.map((link) =>
                link.hasMega ? (
                  <button
                    key={link.href}
                    onClick={() => setMobileMegaOpen(true)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                      location.startsWith("/services")
                        ? "text-[#66CDAA] bg-white/5"
                        : "text-white/80 hover:text-white hover:bg-white/5"
                    }`}
                  >
                    {link.label}
                    <ChevronDown className="w-4 h-4" />
                  </button>
                ) : (
                  <Link
                    key={link.href}
                    href={link.href}
                    className={`block px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                      location === link.href
                        ? "text-[#66CDAA] bg-white/5"
                        : "text-white/80 hover:text-white hover:bg-white/5"
                    }`}
                  >
                    {link.label}
                  </Link>
                )
              )}
              <div className="pt-4">
                <Link
                  href="/contact"
                  className="glow-btn block text-center text-base py-3"
                >
                  דברו איתנו
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}
